import { UAContext } from "@quentin-sommer/react-useragent";
import flat from "flat";
import { LoaderCircle } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import TextField from "~/components/Fields/TextField";
import { SheetOrModal } from "~/components/Modal";
import Button from "~/design/ui/Button";
import toastWithStyle from "~/helpers/toastWithStyle";
import { patchResource } from "~/services/api";
import to from "~/utils/to";

function EditEmail({ visible, onClose, cookie, user }: { visible: boolean; onClose: () => void; cookie: any; user: any }) {
  const [submitting, setSubmitting] = React.useState(false);
  const { uaResults } = React.useContext(UAContext);
  const { t, i18n } = useTranslation();
  const [data, setData] = React.useState({ "email.address": user?.email.address || "" });

  React.useEffect(() => {
    setData({ "email.address": user?.email.address || "" });
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const [err] = await to(patchResource({ resource: "users", resourceId: user._id, token: cookie.token, data: flat.unflatten(data, { safe: true }) }));
    setSubmitting(false);

    if (err) return toastWithStyle({ message: err.response?.data?.message, options: { type: "error" }, uaResults, i18n });

    toastWithStyle({ message: t("modals.editEmail.successMessage"), options: { type: "success" }, uaResults, i18n });
    onClose();
  };

  const Content = React.useMemo(() => {
    return (
      <form className="flex flex-col" aria-disabled={submitting} onSubmit={onSubmit}>
        <div className="flex flex-col items-center text-center">
          <h2 className="font-display text-xl font-bold leading-relaxed text-ink">{t("modals.editEmail.email")}</h2>
          <p className="mt-1 max-w-[34ch] text-sm leading-7 text-ink-2">{t("modals.editEmail.editYourEmail")}</p>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <TextField
            label={{ text: t("modals.editEmail.email") }}
            input={{
              type: "text",
              name: "email.address",
              value: data["email.address"],
              placeholder: t("modals.editEmail.emailPlaceholder"),
              autoComplete: "email",
              onChange: handleInputChange,
              disabled: submitting,
            }}
            errorMode="subtitle"
          />
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <Button
            type="submit"
            size="lg"
            block
            disabled={submitting || !data["email.address"] || data["email.address"] === user?.email.address}
            aria-busy={submitting}
          >
            {submitting && <LoaderCircle className="size-5 animate-spin" strokeWidth={1.75} />}
            {submitting ? t("modals.deleteUserConfirmation.pleaseWait") : t("modals.editEmail.saveChanges")}
          </Button>
          <Button variant="ghost" size="lg" block onClick={onClose}>
            {t("modals.editEmail.closeButton")}
          </Button>
        </div>
      </form>
    );
  }, [data, onClose, submitting, t]);

  return (
    <SheetOrModal visible={visible} onClose={onClose} label={t("modals.editEmail.email")}>
      {Content}
    </SheetOrModal>
  );
}

export default EditEmail;
